/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode, useContext, useState } from 'react';
import { Dimensions, StyleProp, StyleSheet, View } from 'react-native';
import DyteClient, { DyteParticipant, DyteSelf } from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import PeerView from '../PeerView';
import DyteNameTag from './DyteNameTag';
import { SvgXml } from 'react-native-svg';
import { defaultIconPack, IconPack } from '../lib/icons';
import { defaultConfig } from '../lib/default-ui-config';
import { UIConfig } from '../types/ui-config';
import { DyteI18n, useLanguage } from '../lib/lang';
import { States } from '../types/props';

import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useGridItemDimensions } from '../utils/grid';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  participant,
  config = defaultConfig,
  meeting,
  nameTagPosition = 'bottom-left',
  style,
  isPreview = false,
  iconPack = defaultIconPack,
  size = 'sm',
  states,
  t,
  children,
}: {
  meeting: DyteClient;
  participant: DyteParticipant | DyteSelf;
  config?: UIConfig;
  style?: StyleProp<any>;
  nameTagPosition?:
    | 'bottom-center'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-center'
    | 'top-left'
    | 'top-right'
    | 'none';
  isPreview?: boolean;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  states?: States;
  t?: DyteI18n;
  children?: ReactNode;
}) {
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const self = useDyteSelector((m) => m.self);
  const isSelf = participant.id === self.id;
  const activeParticipants = useDyteSelector((m) => m.participants.active);
  const activeParticipantCount = activeParticipants.toArray().length;
  const insets = useSafeAreaInsets();
  const { width, height } = Dimensions.get('screen');
  const calcVals = useGridItemDimensions({
    count: activeParticipantCount,
    dimensions: {
      height: height - insets.bottom - insets.top,
      width: width,
    },
    aspectRatio: '9:16',
    gap: 8,
  });
  if (!participant && isSelf) participant = self;
  if (!participant) return null;
  const position =
    nameTagPosition !== undefined ? nameTagPosition : 'bottom-left';
  const styles = StyleSheet.create({
    container: {
      ...style,
      margin: 5,
    },
    nameTagContainer: {
      position: 'absolute',
    },
    bottomLeftContainer: {
      position: 'absolute',
      bottom: 10,
      left: 10,
    },
    bottomCenterContainer: {
      position: 'absolute',
      bottom: 10,
      marginHorizontal: '30%',
    },
    bottomRightContainer: {
      position: 'absolute',
      bottom: 10,
      right: 10,
    },
    topLeftContainer: {
      position: 'absolute',
      top: 10,
      left: 10,
    },
    topCenterContainer: {
      position: 'absolute',
      top: 10,
      marginHorizontal: '30%',
    },
    topRightContainer: {
      position: 'absolute',
      top: 10,
      right: 10,
    },
    iconStyle: {
      padding: 4,
      backgroundColor: colors.background[900],
    },
  });
  return (
    <View style={styles.container}>
      <PeerView
        participant={participant ?? meeting.self}
        width={style?.width ?? calcVals.width}
        height={style?.height ?? calcVals.height}
      />
      {participant.isPinned && (
        <View style={styles.topLeftContainer}>
          <View style={styles.iconStyle}>
            <SvgXml
              xml={iconPack.pin.replace('currentColor', colors.text)}
              width={20}
              height={20}
            />
          </View>
        </View>
      )}
      {position === 'bottom-left' && (
        <View style={styles.bottomLeftContainer}>
          <DyteNameTag meeting={meeting} participant={participant}>
            {participant.audioEnabled ? (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_on.replace('currentColor', colors.text)}
                  width={16}
                  height={16}
                />
              </View>
            ) : (
              /* <DyteAudioVisualizer participant={participant} />*/
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_off.replace('currentColor', colors.danger)}
                  width={16}
                  height={16}
                />
              </View>
            )}
          </DyteNameTag>
        </View>
      )}
      {position === 'bottom-center' && (
        <View style={styles.bottomCenterContainer}>
          <DyteNameTag meeting={meeting} participant={participant}>
            {participant.audioEnabled ? (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_on.replace('currentColor', colors.text)}
                  width={16}
                  height={16}
                />
              </View>
            ) : (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_off.replace('currentColor', colors.danger)}
                  width={16}
                  height={16}
                />
              </View>
            )}
          </DyteNameTag>
        </View>
      )}
      {position === 'bottom-right' && (
        <View style={styles.bottomRightContainer}>
          <DyteNameTag meeting={meeting} participant={participant}>
            {participant.audioEnabled ? (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_on.replace('currentColor', colors.text)}
                  width={16}
                  height={16}
                />
              </View>
            ) : (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_off.replace('currentColor', colors.danger)}
                  width={16}
                  height={16}
                />
              </View>
            )}
          </DyteNameTag>
        </View>
      )}
      {position === 'top-left' && (
        <View style={styles.topLeftContainer}>
          <DyteNameTag meeting={meeting} participant={participant}>
            {participant.audioEnabled ? (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_on.replace('currentColor', colors.text)}
                  width={16}
                  height={16}
                />
              </View>
            ) : (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_off.replace('currentColor', colors.danger)}
                  width={16}
                  height={16}
                />
              </View>
            )}
          </DyteNameTag>
        </View>
      )}
      {position === 'top-center' && (
        <View style={styles.topCenterContainer}>
          <DyteNameTag meeting={meeting} participant={participant}>
            {participant.audioEnabled ? (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_on.replace('currentColor', colors.text)}
                  width={16}
                  height={16}
                />
              </View>
            ) : (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_off.replace('currentColor', colors.danger)}
                  width={16}
                  height={16}
                />
              </View>
            )}
          </DyteNameTag>
        </View>
      )}
      {position === 'top-right' && (
        <View style={styles.topRightContainer}>
          <DyteNameTag meeting={meeting} participant={participant}>
            {participant.audioEnabled ? (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_on.replace('currentColor', colors.text)}
                  width={16}
                  height={16}
                />
              </View>
            ) : (
              <View style={styles.iconStyle}>
                <SvgXml
                  xml={iconPack.mic_off.replace('currentColor', colors.danger)}
                  width={16}
                  height={16}
                />
              </View>
            )}
          </DyteNameTag>
        </View>
      )}
      {children}
    </View>
  );
}
